<template>
<div class="x-y-pie-chart-wrapper" :class="{
  'show-pie': isShowPie
}">
  <x-y-chart
    ref="xyChart"
    :index="index"
    :data-config="dataConfig"
    :unit-prop="unitProp"
    :chart-type="chartType"
    :stack-type="stackType"
    :is-show-bar="isShowPie"
    :download-name="downloadName"
    :data-time-format="dataTimeFormat"
    chart-min-height="400px"
    chart-height="auto"
    need-resize
    style="flex:1 0 100%;"
    :click-handler="onXYChartClick"
  />

  <div
    v-if="isShowPie"
    class="pie-chart-wrapper"
  >
    <div style="display:flex;justify-content:center;">
      <el-radio-group v-model="pieChartType" size="small" @change="onPieChartModeChange">
        <el-radio-button :label="2">2D</el-radio-button>
        <el-radio-button :label="3">3D</el-radio-button>
      </el-radio-group>
    </div>

    <div style="display:flex;justify-content:center;">
      <el-select
        size="small"
        v-model="chartTime"
        style="width:180px;"
        @change="onChartTimeChange"
      >
        <el-option
          v-for="item in dataConfig.dataTimes"
          :key="item[1]"
          :label="item[1]"
          :value="item[1]"
        />
      </el-select>

      <el-checkbox
        border
        size="small"
        label="显示均值"
        v-model="isShowAvg"
        style="margin-left: .5em;"
        @change="isShowAvgPieChange"
      />
    </div>

    <div ref="highChart" class="pie-wrapper"></div>
  </div>
</div>
</template>

<script>
import XYChart from '../../../../components/DataChart/XYChart'
import Highcharts from 'highcharts'
import highcharts3d from 'highcharts/highcharts-3d'
import exporting from 'highcharts/modules/exporting'
import ChartStyle from '../../../../components/DataChart/ChartStyle'

exporting(Highcharts)
highcharts3d(Highcharts)

export default {
  name: 'XYPieChart',
  components: {XYChart},
  props: {
    dataConfig: {
      required: true,
      type: Object
    },
    index: {
      required: true,
      type: Number
    },
    unitProp: {
      required: true,
      type: String
    },
    chartType: {
      required: true,
      type: String
    },
    stackType: {
      type: Number,
      default: 1
    },
    downloadName: {
      required: true,
      type: String
    },
    chartMinHeight: {
      type: String,
      default: '200px'
    },
    chartHeight: {
      type: String,
      default: '300px'
    },
    needResize: {
      type: Boolean
    },
    isShowPie: {
      required: true,
      type: Boolean
    },
    pieDimensionIds: {
      type: Array
    },
    dataTimeFormat: {
      type: String,
      // default: 'YY年MM月DD日HH时'
      default: 'YY/MM/DD HH'
    }
  },
  data: () => ({
    pieChartType: 2,
    chartTime: null,
    isShowAvg: true
  }),
  computed: {
    dimensions () {
      const {pieDimensionIds} = this
      const {COLORS} = ChartStyle
      const COLORS_LEN = COLORS.length
      return Array.isArray(pieDimensionIds) && pieDimensionIds.length > 0 ? this.dataConfig.dimensions.reduce((list, dimension, index) => {
        if (pieDimensionIds.indexOf(dimension.id) > -1) {
          list.push({
            ...dimension,
            color: COLORS[index % COLORS_LEN]
          })
        }

        return list
      }, []) : this.dataConfig.dimensions
    },
    avgData () {
      const {dataConfig, dimensions} = this
      const {dataTimes, seriesMap} = dataConfig
      const avgMap = dimensions.reduce((map, dimension) => {
        const dimensionId = dimension.id
        const dataMap = seriesMap[dimensionId]
        const count = dataTimes.reduce((count, dataTime) => {
          const data = dataMap[dataTime[1]]

          if (data) {
            count.total += data.value
            count.length++
          }

          return count
        }, {
          total: 0,
          length: 0
        })

        const avg = count.total / count.length
        map[dimensionId] = Number.isFinite(avg) ? Number(avg.toFixed(3)) : null
        return map
      }, {})

      return dataTimes.reduce((map, [ts, dataTime]) => {
        map[dataTime] = dimensions.reduce((map, dimension) => {
          const dimensionId = dimension.id
          const data = seriesMap[dimensionId][dataTime]

          if (data) {
            map[dimensionId] = data.value
          }

          return map
        }, {})
        return map
      }, {
        avg: avgMap
      })
    }
  },
  methods: {
    renderPieChart () {
      if (!this.isShowPie) {
        return
      }

      const {dimensions, avgData, pieChartType} = this
      const key = this.chartTime || 'avg'
      const currentData = avgData[key]
      const seriesData = dimensions.map(dimension => ({
        name: dimension.name,
        selected: true,
        sliced: true,
        y: currentData[dimension.id],
        value: currentData[dimension.id]
      }))

      this.highChart = Highcharts.chart(this.$refs['highChart'], {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          type: 'pie',
          options3d: {
            enabled: pieChartType === 3,
            alpha: 45,
            beta: 0
          }
        },
        title: {
          text: '',
          style: {
            fontWeight: 'bold'
          }
        },
        legend: {
          align: 'center',
          verticalAlign: 'top',
          x: 0,
          y: 20
        },
        colors: dimensions.map(dimension => dimension.color),
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.2f}%</b>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            size: '65%',
            depth: pieChartType === 3 ? 35 : 0,
            dataLabels: {
              enabled: true,
              distance: '15%',
              format: '<b>{point.name}</b>: <br /></b>{point.percentage:.2f} %',
              style: {
                color: (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black'
              }
            }
          }
        },
        exporting: {
          enabled: true
        },
        lang: {
          contextButtonTitle: '菜单',
          viewFullscreen: '全屏',
          exitFullscreen: '退出全屏',
          printChart: '打印图表',
          downloadPNG: '下载PNG图片',
          downloadJPEG: '下载JPEG图片',
          downloadPDF: '下载PDF文件',
          downloadSVG: '下载SVG图片'
        },
        series: [{
          name: '平均值',
          opacity: 0.9,
          showInLegend: true,
          data: seriesData
        }]
      })
    },
    onXYChartClick (param) {
      this.chartTime = param.name
      this.isShowAvg = false
      this.renderPieChart()
    },
    onChartTimeChange () {
      this.isShowAvg = false
      this.renderPieChart()
    },
    onPieChartModeChange () {
      this.renderPieChart()
    },
    isShowAvgPieChange () {
      this.chartTime = null
      this.renderPieChart()
    }
  },
  watch: {
    isShowPie () {
      this.renderPieChart()
    },
    dataConfig () {
      this.renderPieChart()
    }
  },
  mounted () {
    setTimeout(() => {
      this.renderPieChart()
    }, 0)
  }
}
</script>

<style lang="scss" scoped>
.x-y-pie-chart-wrapper {
  display: grid;
  grid-template-columns: auto;
  flex: 1 0 100%;
  min-width: 0;
  height: 100%;

  &.show-pie {
    grid-template-columns: auto 45%;
  }
}

.pie-chart-wrapper {
  display: grid;
  grid-template-rows: max-content 30px auto;
  grid-row-gap: 10px;
  max: {
    width: 100%;
    height: 100%;
  }
}

.pie-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min: {
    width: 0;
    height: 0;
  };
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/00PM/05PM25-BC-Ratio/XYPieChart.vue